import React, { useState } from "react";
import Footer from "../components/footer";
import Header from "../components/header";
import { useTranslation } from "react-i18next";

export default function DeleteRequest() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    reason: ""
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = t("UsernameRequired");
    }
    
    if (!formData.password.trim()) {
      newErrors.password = t("PasswordRequired");
    }
    
    if (!formData.reason.trim()) {
      newErrors.reason = t("ReasonRequired");
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Form başarıyla gönderildi
      setIsSubmitted(true);
      
      // Formu temizle
      setFormData({
        username: "",
        password: "",
        reason: ""
      });
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col min-h-screen bg-white">
        <Header isHomePage={false} />
        
        {/* Success Message */}
        <div className="flex-1 flex items-center justify-center px-4 pt-32">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("RequestSubmittedSuccessfully")}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {t("RequestSubmittedMessage")}
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="w-full bg-[#FCB8C4] text-white py-2 px-4 rounded-lg hover:bg-[#f8a5b3] transition-colors"
            >
              {t("SendNewRequest")}
            </button>
          </div>
        </div>
        
        <Footer isHomePage={false} />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header isHomePage={false} />
      
      {/* Hero Section */}
      <section className="relative w-full h-[220px] sm:h-[220px] md:h-[440px] lg:h-[440px] xl:h-[400px] 2xl:h-[500px] overflow-hidden">
        <div className="absolute inset-0 flex items-end bg-[#FCB8C4]">
          <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-28 2xl:px-32 pb-4 sm:pb-8 md:pb-12 lg:pb-16 xl:pb-20 2xl:pb-24">
            <h2 className="text-3xl sm:text-3xl md:text-[42px] lg:text-[48px] xl:text-[54px] 2xl:text-[60px] font-bold font-poppins text-white text-start sm:text-left max-w-xs sm:max-w-sm md:max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto sm:mx-0">
              {t("DeleteRequestTitle")}
            </h2>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 md:py-16 lg:py-20">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
              {t("DeleteAccountQuestion")}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Field */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Username")} *
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FCB8C4] ${
                    errors.username ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t("UsernamePlaceholder")}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Password")} *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FCB8C4] ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t("PasswordPlaceholder")}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Reason Field */}
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("DeleteReason")} *
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FCB8C4] ${
                    errors.reason ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t("ReasonPlaceholder")}
                />
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-[#FCB8C4] text-white py-3 px-4 rounded-lg hover:bg-[#f8a5b3] transition-colors font-medium"
              >
                {t("SubmitDeleteRequest")}
              </button>
            </form>

            {/* Warning Message */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>{t("Warning")}:</strong> {t("WarningMessage")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer isHomePage={false} />
    </div>
  );
} 